import { useState, lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { fadeInUp } from "@/lib/motion";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/layout/PageHeader";
import { useLanguage } from "@/components/LanguageContext";
import { SEO } from "@/components/layout/SEO";
import { MapPin, Mail, Phone, Clock } from "iconoir-react";

const LocationMap = lazy(() => import("@/components/map/LocationMap"));

export default function Contact() {
  const { t } = useLanguage();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "",
    organization: "",
    email: "",
    inquiry: "Mobility & Transport Study",
    message: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const inquiryParam = params.get("inquiry");
    if (inquiryParam) {
      setFormData(prev => ({ ...prev, inquiry: inquiryParam }));
    }
  }, [location.search]);

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    
    try {
      const subject = encodeURIComponent(`${formData.inquiry} - ${formData.organization}`);
      const body = encodeURIComponent(
        `Nom: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Organisation: ${formData.organization}\n` +
        `Type de Demande: ${formData.inquiry}\n\n` +
        `Message:\n${formData.message}`
      );
      
      window.location.href = `mailto:contact@avisium.ma?subject=${subject}&body=${body}`;
      
      setStatus('success');
      setFormData({ name: "", organization: "", email: "", inquiry: "Mobility & Transport Study", message: "" });
      
      setTimeout(() => setStatus('idle'), 5000);
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <>
      <SEO 
        title={t("Contact", "Contact")}
        description={t(
          "Contactez AVISIUM Solutions Group pour vos projets de mobilité, d'infrastructure et d'intelligence territoriale. Parlons de votre vision.",
          "Contact AVISIUM Solutions Group for your mobility, infrastructure, and territorial intelligence projects. Let's talk about your vision."
        )}
      />
      <PageHeader 
        overlineFr="Nous Contacter"
        overlineEn="Get in Touch"
        titleFr="Construisons l'Avenir de la Mobilité."
        titleEn="Let's Build the Future of Mobility."
        animatedGif={true}
      />

      <section className="py-20 lg:py-32 bg-warm-white relative">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
            
            <motion.div 
              variants={fadeInUp} 
              initial="hidden" 
              animate="visible" 
              className="lg:col-span-7"
            >
               <div className="bg-warm-white rounded-3xl p-8 lg:p-12 border border-mineral shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)]">
                 <h2 className="text-2xl font-display font-semibold text-charcoal mb-8">
                   {t("Envoyez-nous un message", "Send us a message")}
                 </h2>
                 <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="text-sm font-medium text-graphite block">
                           {t("Nom Complet", "Full Name")}
                         </label>
                         <input 
                            type="text" 
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="w-full bg-mineral/30 border border-mineral-dark rounded-xl px-4 py-3 text-charcoal focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-all" 
                            placeholder="John Doe" 
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-sm font-medium text-graphite block">
                           {t("Organisation", "Organization")}
                         </label>
                         <input 
                            type="text" 
                            value={formData.organization}
                            onChange={(e) => setFormData({...formData, organization: e.target.value})}
                            className="w-full bg-mineral/30 border border-mineral-dark rounded-xl px-4 py-3 text-charcoal focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-all" 
                            placeholder="City Council" 
                         />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                         <label className="text-sm font-medium text-graphite block">
                           {t("Adresse Email", "Email Address")}
                         </label>
                         <input 
                            type="email" 
                            required
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            className="w-full bg-mineral/30 border border-mineral-dark rounded-xl px-4 py-3 text-charcoal focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-all" 
                            placeholder="john@example.com" 
                         />
                      </div>
                      <div className="space-y-2">
                         <label className="text-sm font-medium text-graphite block">
                           {t("Type de Demande", "Inquiry Type")}
                         </label>
                         <select 
                            value={formData.inquiry}
                            onChange={(e) => setFormData({...formData, inquiry: e.target.value})}
                            className="w-full bg-mineral/30 border border-mineral-dark rounded-xl px-4 py-3 text-charcoal focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-all appearance-none cursor-pointer text-sm"
                         >
                            <option value="Mobility & Transport Study">{t("Étude Mobilité & Transport", "Mobility & Transport Study")}</option>
                            <option value="GIS & Analytics Platform">{t("Plateforme SIG & Analytique", "GIS & Analytics Platform")}</option>
                            <option value="Software Demo">{t("Démo Logiciel", "Software Demo")}</option>
                            <option value="ZFE & Sustainability">{t("ZFE & Développement Durable", "ZFE & Sustainability")}</option>
                            <option value="Partnership Inquiry">{t("Demande de Partenariat", "Partnership Inquiry")}</option>
                         </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                       <label className="text-sm font-medium text-graphite block">
                         {t("Détails du Message", "Message Details")}
                       </label>
                       <textarea 
                          rows={4} 
                          required
                          value={formData.message}
                          onChange={(e) => setFormData({...formData, message: e.target.value})}
                          className="w-full bg-mineral/30 border border-mineral-dark rounded-xl px-4 py-3 text-charcoal focus:outline-none focus:border-amber focus:ring-1 focus:ring-amber transition-all resize-none text-sm" 
                          placeholder={t("Parlez-nous des défis de votre projet...", "Tell us about your project challenges...")}
                       ></textarea>
                    </div>
                    
                    <div className="flex flex-col items-start gap-4 mt-6">
                      <Button 
                        type="submit" 
                        variant="primary" 
                        size="lg" 
                        className="w-full sm:w-auto px-12"
                        disabled={status === 'loading'}
                      >
                        {status === 'loading' ? t("Envoi en cours...", "Sending...") : t("Soumettre la Demande", "Submit Request")}
                      </Button>
                      
                      {status === 'success' && (
                        <div className="w-full p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm font-medium flex items-center gap-3">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                          {t("Votre message a bien été envoyé. Nous vous recontacterons dans les plus brefs délais.", "Your message has been successfully sent. We will get back to you as soon as possible.")}
                        </div>
                      )}
                      
                      {status === 'error' && (
                        <div className="w-full p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium flex flex-col gap-2">
                          <p>{t("Une erreur est survenue lors de l'envoi de votre message.", "An error occurred while sending your message.")}</p>
                          <p>{t("En cas d'urgence, veuillez utiliser notre email direct :", "In case of emergency, please use our direct email:")} <a href="mailto:contact@avisium.ma" className="underline font-bold text-red-800 hover:text-red-900 transition-colors">contact@avisium.ma</a></p>
                        </div>
                      )}
                    </div>
                 </form>
               </div>
            </motion.div>

            <motion.div 
              variants={fadeInUp} 
              initial="hidden" 
              animate="visible" 
              transition={{ delay: 0.2 }}
              className="lg:col-span-5 space-y-12"
            >
               <div>
                 <h3 className="text-sm uppercase tracking-widest text-amber font-semibold mb-6">Headquarters</h3>
                 <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-mineral flex items-center justify-center text-graphite shrink-0">
                         <MapPin width={20} height={20} />
                      </div>
                      <div>
                        <p className="text-charcoal font-semibold">{t("Siège Social", "Headquarters")}</p>
                        <p className="text-graphite-light text-sm mt-1">Temara, Morocco</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-mineral flex items-center justify-center text-graphite shrink-0">
                         <Mail width={20} height={20} />
                      </div>
                      <div>
                        <p className="font-semibold text-charcoal">{t("Courrier Électronique", "Electronic Mail")}</p>
                        <p className="text-graphite-light text-sm mt-1">contact@avisium.ma</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-mineral flex items-center justify-center text-graphite shrink-0">
                         <Phone width={20} height={20} />
                      </div>
                      <div>
                        <p className="font-semibold text-charcoal">{t("Ligne Directe", "Direct Line")}</p>
                        <p className="text-graphite-light text-sm mt-1">+212 (0) 710 335 353</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-mineral flex items-center justify-center text-graphite shrink-0">
                         <Clock width={20} height={20} />
                      </div>
                      <div>
                        <p className="font-semibold text-charcoal">{t("Heures d'Ouverture", "Office Hours")}</p>
                        <p className="text-graphite-light text-sm mt-1">{t("Lun - Ven: 08:30 - 18:00 (WET)", "Mon - Fri: 08:30 - 18:00 (WET)")}</p>
                      </div>
                    </div>
                 </div>
               </div>

               <div className="w-full h-80 rounded-2xl relative overflow-hidden shadow-sm border border-mineral">
                  <Suspense fallback={<div className="w-full h-full animate-pulse bg-mineral"></div>}>
                    <LocationMap />
                  </Suspense>
               </div>
            </motion.div>

          </div>
        </Container>
      </section>
    </>
  );
}
